import { useContext } from 'react';
import { useQueryClient, useQueries, useQuery, useMutation } from '@tanstack/react-query';
import chalk from 'chalk';

import PrefetchContext from 'core/components/PrefetchContext';

import { VOID_QUERY, VERBOSE_SSR } from 'core/site/constants';


/**
 * PERF:
 * Если в этот файл вносятся существенные изменения или
 * усложняется логика, обязательно необходимо провести
 * нагрузочное тестирование
 */


/**
 * Заполняет кеш QueryClient на этапе префетчинга
 */
function usePrefetch(queryParams) {
  const queryClient = useQueryClient();
  const prefetchContext = useContext(PrefetchContext);
  const hasMultipleQueries = !!queryParams.queries;
  const hasResolvePromises = Array.isArray(prefetchContext.resolvePromises);
  const queries = hasMultipleQueries ? queryParams.queries : [queryParams];
  const queryCache = queryClient.getQueryCache();

  // TODO: 11158 implement mutation logic

  const results = queries.map(query => {
    const isEnabled = query.enabled ?? true;

    // TODO: 11158 if !isEnabled return nothing right here?
    const hasSelect = typeof query.select === 'function';

    const cachedQuery = queryCache.find({
      queryKey: query.queryKey,
      exact: true,
    });

    const queryState = cachedQuery?.state;

    const {
      fetchStatus,
      status,
      dataUpdateCount,
      errorUpdateCount,
      data: queryData,
    } = queryState || {};

    // Формируем дополнительные поля, дублируя логику react-query
    // Отсутствие query в кеше интерпретируем как признак того, что данные ещё загружаются
    // REF: https://github.com/TanStack/query/blob/515803f5fa7ebc35ecb0504e60df9fe7e3bf057f/packages/query-core/src/queryObserver.ts#L578
    const isFetching = fetchStatus === 'fetching' || !cachedQuery;
    const isFetched = dataUpdateCount > 0 || errorUpdateCount > 0;

    // ниже фоллбечимся на !isEnabled, чтобы сэмулировать стандартное поведение лоад-стейта react-query
    // REF: https://tanstack.com/query/v4/docs/framework/react/guides/dependent-queries
    const isLoading = status === 'loading' || !cachedQuery || !isEnabled;
    const isError = status === 'error';

    const extendedState = {
      isError,
      isLoading,
      isFetching,
      isFetched,
    };

    const queryDataIsEmpty = queryData === null || queryData === undefined;

    // Если для query нет данных, запрашиваем их и заполняем
    // prefetchContext значениями, которые будут обработаны в
    // следующей итерации рендера
    if (!isError && hasResolvePromises && isEnabled && queryDataIsEmpty) {
      if (VERBOSE_SSR) {
        console.warn(
          chalk`{magenta Query key: ${JSON.stringify(query.queryKey)}\n}`
        );
      }

      const queryPromise = queryClient.fetchQuery(query);
      prefetchContext.currentPassHasRequests = true;
      prefetchContext.resolvePromises.push(queryPromise);
    }

    return {
      ...queryState,
      ...extendedState,
      data: hasSelect && !queryDataIsEmpty ? query.select(queryData) : queryData,
    };
  });

  return hasMultipleQueries ? results : results[0];
}


export default function useRequest(queryParams) {
  const isPrefetchPhase = !!useContext(PrefetchContext);
  const isMutation = Boolean(queryParams.mutationKey);
  const isQueries = Boolean(queryParams.queries);
  const useReactQuery = isMutation && useMutation || isQueries && useQueries || useQuery;
  const useHook = isPrefetchPhase ? usePrefetch : useReactQuery;

  const queriesData = useHook(queryParams);

  // В ListPage isLoading валидируется по типу, а react-query может возвращать undefined
  // Чтобы избежать ошибок валидации, в таких случаях явно проставляем false
  if (queriesData.isLoading === undefined) queriesData.isLoading = false;

  // Чтобы в компонентах было удобней фоллбечиться на другие значения,
  // в случае использования VOID_QUERY проставляем data в undefined
  return queryParams === VOID_QUERY
    ? { ...queriesData, data: undefined }
    : queriesData;
}
